"use client"

import { useState, useMemo } from "react"
import { Check, Search, X, CheckSquare, Square } from "lucide-react"
import { Input } from "@/components/ui/input"
import { useLocalizedField } from "@/utils/language-utils"
import { useViewManager } from "../view-manager-context"
import { useTranslation } from "@/hooks/use-translation"

/**
 * Side-by-side column visibility grid with:
 * - Alphabetically sorted fields
 * - "All Stages" column
 * - "Select All" / "Clear All" per stage
 * - Narrower field name column to fit more stages
 */
export function StageColumnList() {
  const { t } = useTranslation()
  const { state, dispatch } = useViewManager()
  const getLocalizedField = useLocalizedField()
  const [searchTerm, setSearchTerm] = useState('')

  const stages = state.originalConfig?.stageConfigurations || []
  const fieldsInUse = state.originalConfig?.fieldsInUse || []

  // Collect ALL fields across all stages — memoize the map itself
  const allFieldsMap = useMemo(() => {
    const map = new Map<string, any>()
    for (const f of fieldsInUse) map.set(f.fieldConfigurationId, f)
    for (const stage of stages) {
      for (const f of (stage.fieldConfigurations || [])) {
        map.set(f.fieldConfigurationId, f)
      }
    }
    return map
  }, [fieldsInUse, stages])

  // Sort alphabetically by localized name
  const allFields = useMemo(() => {
    const fields = Array.from(allFieldsMap.values())
    return fields.sort((a, b) => {
      const nameA = (getLocalizedField(a, 'columnName') || a.columnCode || '').toLowerCase()
      const nameB = (getLocalizedField(b, 'columnName') || b.columnCode || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [allFieldsMap, getLocalizedField])

  // Filter by search
  const filteredFields = useMemo(() => {
    if (!searchTerm.trim()) return allFields
    const q = searchTerm.toLowerCase()
    return allFields.filter(f => {
      const name = getLocalizedField(f, 'columnName') || f.columnCode || ''
      return name.toLowerCase().includes(q)
    })
  }, [allFields, searchTerm, getLocalizedField])

  const stageIds = stages.map((s: any) => s.stageId || s.stageCode)
  const stageNames = stages.map((s: any) => getLocalizedField(s, 'title') || s.stageCode)

  // Check if field is visible on ALL stages
  const isVisibleOnAllStages = (fieldId: string) => {
    return stageIds.every((stageId: string) => (state.draftStageColumns[stageId] || []).includes(fieldId))
  }

  const handleToggle = (stageId: string, fieldId: string) => {
    dispatch({ type: 'TOGGLE_COLUMN', payload: { stageId, fieldId } })
  }

  // Toggle field on ALL stages at once
  const handleToggleAllStages = (fieldId: string) => {
    const allVisible = isVisibleOnAllStages(fieldId)
    for (const stageId of stageIds) {
      const current = state.draftStageColumns[stageId] || []
      const isVisible = current.includes(fieldId)
      if (allVisible && isVisible) {
        // Remove from all
        dispatch({ type: 'TOGGLE_COLUMN', payload: { stageId, fieldId } })
      } else if (!allVisible && !isVisible) {
        // Add to all
        dispatch({ type: 'TOGGLE_COLUMN', payload: { stageId, fieldId } })
      }
    }
  }

  // Select All fields for a stage
  const handleSelectAll = (stageId: string) => {
    const allFieldIds = allFields.map(f => f.fieldConfigurationId)
    dispatch({ type: 'REORDER_COLUMNS', payload: { stageId, fieldIds: allFieldIds } })
  }

  // Clear All fields for a stage
  const handleClearAll = (stageId: string) => {
    dispatch({ type: 'REORDER_COLUMNS', payload: { stageId, fieldIds: [] } })
  }

  // Select/Clear All for ALL stages
  const handleSelectAllStages = () => {
    const allFieldIds = allFields.map(f => f.fieldConfigurationId)
    for (const stageId of stageIds) {
      dispatch({ type: 'REORDER_COLUMNS', payload: { stageId, fieldIds: allFieldIds } })
    }
  }

  const handleClearAllStages = () => {
    for (const stageId of stageIds) {
      dispatch({ type: 'REORDER_COLUMNS', payload: { stageId, fieldIds: [] } })
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder={t('search') || "Search columns..."}
            className="pl-8 pr-7 h-8 text-xs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600" aria-label={t('clear') || "Clear search"}>
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <tr className="bg-white">
              <th className="text-left px-2 py-2 font-semibold text-gray-700 w-[130px] min-w-[130px] sticky left-0 bg-white z-20">
                {t('fieldName') || 'Field name'}
              </th>
              {/* All Stages column */}
              <th className="px-1 py-2 font-semibold text-[#0F214B] min-w-[70px] bg-[#E8E6FF]" style={{ textAlign: 'center' }}>
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-[10px] leading-tight">{t('all') || 'All'}</span>
                  <span className="text-[10px] leading-tight">{t('stages' as any) || 'Stages'}</span>
                </div>
              </th>
              {stageNames.map((name: string, idx: number) => (
                <th
                  key={stageIds[idx]}
                  className={`px-1 py-2 font-medium min-w-[70px] ${
                    state.activeEditStage === stageIds[idx] ? 'text-blue-700 bg-blue-100' : 'text-gray-500 bg-white'
                  }`}
                  style={{ textAlign: 'center' }}
                >
                  <span className="text-[10px] leading-tight block" title={name}>{name}</span>
                </th>
              ))}
            </tr>
            {/* Select All / Clear All row */}
            <tr className="bg-gray-50 border-b border-gray-300">
              <td className="px-2 py-1.5 sticky left-0 bg-gray-50 z-20" />
              {/* All Stages bulk */}
              <td className="px-1 py-1.5 text-center bg-[#E8E6FF]">
                <div className="flex items-center justify-center gap-2">
                  <button onClick={handleSelectAllStages} className="flex items-center gap-0.5 text-green-600 hover:text-green-700" title={t('selectAll') || "Select all"}>
                    <CheckSquare size={13} />
                    <span className="text-[10px] font-medium">{t('all') || 'All'}</span>
                  </button>
                  <button onClick={handleClearAllStages} className="flex items-center gap-0.5 text-red-500 hover:text-red-600" title={t('clearAll') || "Clear all"}>
                    <Square size={13} />
                    <span className="text-[10px] font-medium">{t('vmNone' as any) || 'None'}</span>
                  </button>
                </div>
              </td>
              {stageIds.map((stageId: string) => (
                <td key={stageId} className={`px-1 py-1.5 text-center ${state.activeEditStage === stageId ? 'bg-blue-100/60' : ''}`}>
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => handleSelectAll(stageId)} className="flex items-center gap-0.5 text-green-600 hover:text-green-700" title={t('selectAll') || "Select all"}>
                      <CheckSquare size={13} />
                      <span className="text-[10px] font-medium">{t('all') || 'All'}</span>
                    </button>
                    <button onClick={() => handleClearAll(stageId)} className="flex items-center gap-0.5 text-red-500 hover:text-red-600" title={t('clearAll') || "Clear all"}>
                      <Square size={13} />
                      <span className="text-[10px] font-medium">{t('vmNone' as any) || 'None'}</span>
                    </button>
                  </div>
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredFields.map(field => {
              const fieldId = field.fieldConfigurationId
              const name = getLocalizedField(field, 'columnName') || field.columnCode || fieldId
              const allStagesChecked = isVisibleOnAllStages(fieldId)
              return (
                <tr key={fieldId} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-2 py-1 text-gray-700 font-medium sticky left-0 bg-white truncate max-w-[130px] text-[11px]" title={name}>
                    {name}
                  </td>
                  {/* All Stages checkbox */}
                  <td className="px-1 py-1 text-center bg-[#EDEBFF]">
                    <button
                      onClick={() => handleToggleAllStages(fieldId)}
                      className={`
                        inline-flex items-center justify-center w-[18px] h-[18px] rounded border transition-colors
                        ${allStagesChecked
                          ? 'bg-[#0F214B] border-[#0F214B] text-white'
                          : 'bg-white border-gray-300 hover:border-gray-400'
                        }
                      `}
                    >
                      {allStagesChecked && <Check size={11} strokeWidth={3} />}
                    </button>
                  </td>
                  {stageIds.map((stageId: string) => {
                    const isVisible = (state.draftStageColumns[stageId] || []).includes(fieldId)
                    const isActiveStage = state.activeEditStage === stageId
                    return (
                      <td key={stageId} className={`px-1 py-1 text-center ${isActiveStage ? 'bg-blue-50' : ''}`}>
                        <button
                          onClick={() => handleToggle(stageId, fieldId)}
                          role="checkbox"
                          aria-checked={isVisible}
                          aria-label={`${name} - ${getLocalizedField(stages.find((s: any) => (s.stageId || s.stageCode) === stageId) || {}, 'title') || stageId}`}
                          className={`
                            inline-flex items-center justify-center w-[18px] h-[18px] rounded border transition-colors
                            ${isVisible
                              ? 'bg-[#0F214B] border-[#0F214B] text-white'
                              : 'bg-white border-gray-300 hover:border-gray-400'
                            }
                          `}
                        >
                          {isVisible && <Check size={11} strokeWidth={3} />}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {filteredFields.length === 0 && (
              <tr>
                <td colSpan={stageIds.length + 2} className="text-center py-6 text-gray-400">
                  {t('noRecordsFound') || 'No columns found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
